"use client"

import { useState, useEffect, useMemo, useRef } from "react"
import {
  format,
  parseISO,
  startOfWeek,
  startOfMonth,
  differenceInMinutes,
  subDays,
} from "date-fns"
import { es } from "date-fns/locale"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts"
import { CalendarIcon, Download, Eye, FileText, Filter, RefreshCw, Search } from "lucide-react"
import "./Recaudaciones.css"
import { fetchData } from "../../services/recaudacionService"
import { getTiposTarifaPorSucursal } from "../../services/tarifasService"
import { jsPDF } from "jspdf"
import autoTable from "jspdf-autotable"
import * as XLSX from "xlsx"

// Componente de calendario simple
const SimpleCalendar = ({ selectedRange, onSelectRange, onClose }) => {
  const [month, setMonth] = useState(new Date())
  const [hoveredDate, setHoveredDate] = useState(null)

  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const days = []
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i))
    }
    return days
  }

  const getWeeksInMonth = (date) => {
    const days = getDaysInMonth(date)
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1)
    const firstDayOfWeek = firstDayOfMonth.getDay() === 0 ? 7 : firstDayOfMonth.getDay() // Lunes como primer día

    const daysFromPrevMonth = firstDayOfWeek - 1
    if (daysFromPrevMonth > 0) {
      const prevMonth = new Date(date.getFullYear(), date.getMonth(), 0)
      for (let i = daysFromPrevMonth; i > 0; i--) {
        days.unshift(new Date(prevMonth.getFullYear(), prevMonth.getMonth(), prevMonth.getDate() - i + 1))
      }
    }

    const lastDay = days[days.length - 1]
    const lastDayOfWeek = lastDay.getDay() === 0 ? 7 : lastDay.getDay()
    if (lastDayOfWeek < 7) {
      const daysToAdd = 7 - lastDayOfWeek
      const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1)
      for (let i = 1; i <= daysToAdd; i++) {
        days.push(new Date(nextMonth.getFullYear(), nextMonth.getMonth(), i))
      }
    }

    const weeks = []
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7))
    }
    return weeks
  }

  const handleDateClick = (date) => {
    if (!selectedRange.from || (selectedRange.from && selectedRange.to)) {
      onSelectRange({ from: date, to: null })
    } else {
      if (date < selectedRange.from) {
        onSelectRange({ from: date, to: selectedRange.from })
      } else {
        onSelectRange({ from: selectedRange.from, to: date })
      }
    }
  }

  const handleMouseEnter = (date) => {
    if (selectedRange.from && !selectedRange.to) {
      setHoveredDate(date)
    }
  }

  const handleMouseLeave = () => {
    setHoveredDate(null)
  }

  const isInRange = (date) => {
    if (!selectedRange.from) return false
    const end = selectedRange.to || hoveredDate
    if (!end) return false

    const start = selectedRange.from < end ? selectedRange.from : end
    const finalEnd = selectedRange.from > end ? selectedRange.from : end
    return date >= start && date <= finalEnd
  }

  const isRangeStart = (date) => selectedRange.from && date.getTime() === selectedRange.from.getTime()
  const isRangeEnd = (date) => selectedRange.to && date.getTime() === selectedRange.to.getTime()

  const prevMonth = () => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))
  const nextMonth = () => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))

  const weeks = getWeeksInMonth(month)

  return (
    <div className="simple-calendar">
      <div className="calendar-header">
        <button type="button" className="calendar-nav-btn" onClick={prevMonth}>
          &lt;
        </button>
        <div className="calendar-title">{format(month, "MMMM yyyy", { locale: es })}</div>
        <button type="button" className="calendar-nav-btn" onClick={nextMonth}>
          &gt;
        </button>
      </div>
      <div className="calendar-weekdays">
        <div>Lu</div>
        <div>Ma</div>
        <div>Mi</div>
        <div>Ju</div>
        <div>Vi</div>
        <div>Sa</div>
        <div>Do</div>
      </div>
      <div className="calendar-days">
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="calendar-week">
            {week.map((day, dayIndex) => {
              const isCurrentMonth = day.getMonth() === month.getMonth()
              const isSelected = isRangeStart(day) || isRangeEnd(day)
              const inRange = isInRange(day)
              return (
                <div
                  key={dayIndex}
                  className={`calendar-day ${!isCurrentMonth ? "other-month" : ""} ${isSelected ? "selected" : ""} ${
                    inRange ? "in-range" : ""
                  } ${isRangeStart(day) ? "range-start" : ""} ${isRangeEnd(day) ? "range-end" : ""}`}
                  onClick={() => handleDateClick(day)}
                  onMouseEnter={() => handleMouseEnter(day)}
                  onMouseLeave={handleMouseLeave}
                >
                  {day.getDate()}
                </div>
              )
            })}
          </div>
        ))}
      </div>
      <div className="calendar-footer">
        <button type="button" className="calendar-btn" onClick={onClose}>
          Aplicar
        </button>
      </div>
    </div>
  )
}

const Recaudaciones = () => {
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState("semana")
  const [filteredTickets, setFilteredTickets] = useState([])
  const [dateRange, setDateRange] = useState({ from: startOfWeek(new Date(), { weekStartsOn: 1 }), to: new Date() })
  const [searchTerm, setSearchTerm] = useState("")
  const [tiposTarifa, setTiposTarifa] = useState([])
  const [selectedTarifa, setSelectedTarifa] = useState("Todas")
  const [sucursalInfo, setSucursalInfo] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [showCalendar, setShowCalendar] = useState(false)
  const calendarRef = useRef(null)
  const [showEmployeeModal, setShowEmployeeModal] = useState(false)
  const [employeeRecaudacion, setEmployeeRecaudacion] = useState([])
  const [totalesGenerales, setTotalesGenerales] = useState({
    totalGeneral: 0,
    totalEfectivo: 0,
    totalTransferencia: 0,
    totalTarjeta: 0,
    totalTickets: 0,
  })

  // Cerrar el calendario al hacer clic fuera de él
  useEffect(() => {
    function handleClickOutside(event) {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [calendarRef])

  // useEffect para cargar tipos de tarifa
  useEffect(() => {
    const fetchTiposTarifa = async () => {
      try {
        const idSucu = localStorage.getItem("idsucursal")
        if (idSucu) {
          const data = await getTiposTarifaPorSucursal(idSucu)
          setTiposTarifa(data)
        }
      } catch (error) {
        console.error("Error al cargar tipos de tarifa por sucursal:", error)
      }
    }
    fetchTiposTarifa()
  }, [])

  // useEffect para obtener datos del API. Se ejecuta al inicio y cada vez que el rango de fechas cambia.
  useEffect(() => {
    const obtenerTickets = async () => {
      if (!dateRange.from || !dateRange.to) return // No hacer nada si las fechas no están completas

      try {
        setLoading(true)
        const data = await fetchData({ fechaInicio: dateRange.from, fechaFin: dateRange.to, idTipoTarifa: selectedTarifa })
        setTickets(data)
      } catch (error) {
        console.error("Error al obtener tickets:", error)
        setTickets([]) // Limpiar tickets en caso de error para no mostrar datos viejos
      } finally {
        setLoading(false)
      }
    }

    obtenerTickets()
  }, [dateRange, selectedTarifa]) // La dependencia ahora incluye la tarifa

  // useEffect para el filtro por búsqueda (el filtro por fecha ya no es necesario aquí).
  useEffect(() => {
    if (!tickets) {
      setFilteredTickets([])
      return
    }

    let filteredData = [...tickets]

    // Filtrar por término de búsqueda (esto se queda)
    if (searchTerm) {
      filteredData = filteredData.filter(
        (ticket) =>
          ticket.placa.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (ticket.registro_salida && ticket.registro_salida.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (ticket.registro_entrada && ticket.registro_entrada.toLowerCase().includes(searchTerm.toLowerCase())),
      )
    }

    setFilteredTickets(filteredData)
  }, [tickets, searchTerm]) // Las dependencias ahora son solo los tickets y el término de búsqueda

  // Función para manejar el cambio en los botones de rango de tiempo (Hoy, Ayer, etc.)
  const handleTimeRangeChange = (value) => {
    setTimeRange(value)
    const today = new Date()

    if (value === "personalizado") {
      setShowCalendar(true) // Abrir calendario en modo personalizado
      return
    }

    let fromDate, toDate
    switch (value) {
      case "hoy":
        fromDate = toDate = today
        break
      case "ayer":
        fromDate = toDate = subDays(today, 1)
        break
      case "semana":
        fromDate = startOfWeek(today, { weekStartsOn: 1 })
        toDate = today
        break
      case "mes":
        fromDate = startOfMonth(today)
        toDate = today
        break
      default:
        return
    }
    setDateRange({ from: fromDate, to: toDate })
  }

  // handleRefresh actualizado
  const handleRefresh = async () => {
    if (!dateRange.from || !dateRange.to) return

    try {
      setRefreshing(true)
      const data = await fetchData({ fechaInicio: dateRange.from, fechaFin: dateRange.to })
      setTickets(data)
    } catch (error) {
      console.error("Error al refrescar:", error)
    } finally {
      setRefreshing(false)
    }
  }

  // Calcular estadísticas
  const stats = useMemo(() => {
    if (filteredTickets.length === 0) {
      return {
        totalRecaudado: 0,
        totalTickets: 0,
        promedioEstadia: 0,
        efectivo: 0,
        transferencia: 0,
        tarjetaCredito: 0,
        ticketsEfectivo: 0,
        ticketsTransferencia: 0,
        ticketsTC: 0,
      }
    }

    const totalRecaudado = filteredTickets.reduce((sum, ticket) => sum + (ticket.monto || 0), 0)
    const ticketsConTiempo = filteredTickets.filter((ticket) => ticket.hentrada && ticket.hsalida)

    const tiempoTotal = ticketsConTiempo.reduce((sum, ticket) => {
      const entrada = parseISO(ticket.hentrada)
      const salida = parseISO(ticket.hsalida)
      return sum + differenceInMinutes(salida, entrada)
    }, 0)

    const promedioEstadia = ticketsConTiempo.length > 0 ? tiempoTotal / ticketsConTiempo.length : 0

    const ticketsEfectivo = filteredTickets.filter((ticket) => ticket.tipo_pago === "E")
    const ticketsTransferencia = filteredTickets.filter((ticket) => ticket.tipo_pago === "T")
    const ticketsTC = filteredTickets.filter((ticket) => ticket.tipo_pago === "TC")

    const efectivo = ticketsEfectivo.reduce((sum, ticket) => sum + (ticket.monto || 0), 0)
    const transferencia = ticketsTransferencia.reduce((sum, ticket) => sum + (ticket.monto || 0), 0)
    const tarjetaCredito = ticketsTC.reduce((sum, ticket) => sum + (ticket.monto || 0), 0)

    return {
      totalRecaudado,
      totalTickets: filteredTickets.length,
      promedioEstadia,
      efectivo,
      transferencia,
      tarjetaCredito,
      ticketsEfectivo: ticketsEfectivo.length,
      ticketsTransferencia: ticketsTransferencia.length,
      ticketsTC: ticketsTC.length,
    }
  }, [filteredTickets])

  // Preparar datos para el gráfico diario
  const chartData = useMemo(() => {
    if (filteredTickets.length === 0) return []
    const dailyData = {}
    filteredTickets.forEach((ticket) => {
      const date = ticket.hsalida || ticket.hentrada
      if (!date) return
      const day = format(parseISO(date), "yyyy-MM-dd")
      if (!dailyData[day]) {
        dailyData[day] = {
          fecha: format(parseISO(date), "dd/MM/yyyy"),
          total: 0,
          efectivo: 0,
          transferencia: 0,
          tarjetaCredito: 0,
          tickets: 0,
        }
      }
      dailyData[day].total += ticket.monto || 0
      dailyData[day].tickets += 1
      if (ticket.tipo_pago === "E") dailyData[day].efectivo += ticket.monto || 0
      else if (ticket.tipo_pago === "T") dailyData[day].transferencia += ticket.monto || 0
      else if (ticket.tipo_pago === "TC") dailyData[day].tarjetaCredito += ticket.monto || 0
    })
    return Object.values(dailyData).sort((a, b) => {
      const dateA = parseISO(a.fecha.split("/").reverse().join("-"))
      const dateB = parseISO(b.fecha.split("/").reverse().join("-"))
      return dateA.getTime() - dateB.getTime()
    })
  }, [filteredTickets])

  // Datos para el gráfico de tipo de pago
  const paymentChartData = useMemo(() => {
    return [
      { name: "Efectivo", value: stats.efectivo, color: "#1775ad" },
      { name: "Transferencia", value: stats.transferencia, color: "#933155" },
      { name: "Tarjeta", value: stats.tarjetaCredito, color: "#e17b1d " },
    ]
  }, [stats])

  // Exportar a Excel con numeración secuencial y totales
  const exportToExcel = () => {
    if (filteredTickets.length === 0) return

    const excelData = filteredTickets.map((ticket, index) => {
      const entrada = ticket.hentrada ? parseISO(ticket.hentrada) : null
      const salida = ticket.hsalida ? parseISO(ticket.hsalida) : null
      const duracion = entrada && salida ? differenceInMinutes(salida, entrada) : null

      return {
        "N°": index + 1,
        ID: ticket.id,
        Placa: ticket.placa,
        Entrada: entrada ? format(entrada, "dd/MM/yyyy HH:mm") : "-",
        Salida: salida ? format(salida, "dd/MM/yyyy HH:mm") : "-",
        Duración: duracion !== null ? `${Math.floor(duracion / 60)}h ${duracion % 60}m` : "-",
        Monto: ticket.monto?.toFixed(2) || "0.00",
        "Tipo Pago":
          ticket.tipo_pago === "E"
            ? "Efectivo"
            : ticket.tipo_pago === "T"
            ? "Transferencia"
            : ticket.tipo_pago === "TC"
            ? "Tarjeta"
            : ticket.tipo_pago,
        "Registró Entrada": ticket.registro_entrada || "-",
        "Registró Salida": ticket.registro_salida || "-",
      }
    })

    excelData.push({}) // Fila vacía
    excelData.push({
      Placa: "TOTALES",
      Monto: stats.totalRecaudado.toFixed(2),
    })
    excelData.push({
      Placa: "Efectivo",
      Monto: stats.efectivo.toFixed(2),
    })
    excelData.push({
      Placa: "Transferencia",
      Monto: stats.transferencia.toFixed(2),
    })
    excelData.push({
      Placa: "Tarjeta",
      Monto: stats.tarjetaCredito.toFixed(2),
    })

    const worksheet = XLSX.utils.json_to_sheet(excelData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Recaudaciones")

    const columnWidths = [
      { wch: 5 },
      { wch: 8 },
      { wch: 10 },
      { wch: 18 },
      { wch: 18 },
      { wch: 12 },
      { wch: 10 },
      { wch: 15 },
      { wch: 20 },
      { wch: 20 },
    ]
    worksheet["!cols"] = columnWidths

    XLSX.writeFile(workbook, `recaudaciones_${format(new Date(), "yyyy-MM-dd")}.xlsx`)
  }

  // Exportar a PDF
  const exportToPDF = () => {
    if (filteredTickets.length === 0) return

    const doc = new jsPDF("landscape")
    doc.setFontSize(18)
    doc.text("Reporte de Recaudaciones", 14, 22)
    doc.setFontSize(12)
    doc.text(`Período: ${format(dateRange.from, "dd/MM/yyyy")} - ${format(dateRange.to, "dd/MM/yyyy")}`, 14, 30)

    doc.setFontSize(14)
    doc.text("Resumen", 14, 48)
    doc.setFontSize(10)
    doc.text(`Total Recaudado: $${stats.totalRecaudado.toFixed(2)}`, 14, 56)
    doc.text(`Efectivo: $${stats.efectivo.toFixed(2)} (${stats.ticketsEfectivo} tickets)`, 14, 64)
    doc.text(`Transferencia: $${stats.transferencia.toFixed(2)} (${stats.ticketsTransferencia} tickets)`, 14, 72)
    doc.text(`Tarjeta: $${stats.tarjetaCredito.toFixed(2)} (${stats.ticketsTC} tickets)`, 14, 80)

    const tableColumn = [
      "N°",
      "ID",
      "Placa",
      "Entrada",
      "Salida",
      "Duración",
      "Monto",
      "Tipo Pago",
      "Registró Entrada",
      "Registró Salida",
    ]
    const tableRows = []
    filteredTickets.forEach((ticket, index) => {
      const entrada = ticket.hentrada ? parseISO(ticket.hentrada) : null
      const salida = ticket.hsalida ? parseISO(ticket.hsalida) : null
      const duracion = entrada && salida ? differenceInMinutes(salida, entrada) : null
      const ticketData = [
        index + 1,
        ticket.id,
        ticket.placa,
        entrada ? format(entrada, "dd/MM/yyyy HH:mm") : "-",
        salida ? format(salida, "dd/MM/yyyy HH:mm") : "-",
        duracion !== null ? `${Math.floor(duracion / 60)}h ${duracion % 60}m` : "-",
        `$${ticket.monto?.toFixed(2) || "0.00"}`,
        ticket.tipo_pago === "E"
          ? "Efectivo"
          : ticket.tipo_pago === "T"
          ? "Transferencia"
          : ticket.tipo_pago === "TC"
          ? "Tarjeta"
          : ticket.tipo_pago,
        ticket.registro_entrada || "-",
        ticket.registro_salida || "-",
      ]
      tableRows.push(ticketData)
    })
    tableRows.push(["", "", "TOTALES", "", "", "", `$${stats.totalRecaudado.toFixed(2)}`, "", "", ""])

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 90,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [23, 117, 173], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [240, 240, 240] },
    })

    const pageCount = doc.internal.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.text(
        `Generado el ${format(new Date(), "dd/MM/yyyy HH:mm")} - Página ${i} de ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" },
      )
    }
    doc.save(`recaudaciones_${format(new Date(), "yyyy-MM-dd")}.pdf`)
  }

  const calcularRecaudacionPorEmpleado = () => {
    if (filteredTickets.length === 0) return

    const recaudacionPorEmpleado = {}
    let totalGeneral = 0,
      totalEfectivo = 0,
      totalTransferencia = 0,
      totalTarjeta = 0,
      totalTickets = 0

    filteredTickets.forEach((ticket) => {
      const nombreEmpleado = ticket.registro_salida || "N/A"
      if (!recaudacionPorEmpleado[nombreEmpleado]) {
        recaudacionPorEmpleado[nombreEmpleado] = {
          nombre: nombreEmpleado,
          total: 0,
          efectivo: 0,
          transferencia: 0,
          tarjeta: 0,
          tickets: 0,
        }
      }
      const monto = ticket.monto || 0
      recaudacionPorEmpleado[nombreEmpleado].total += monto
      recaudacionPorEmpleado[nombreEmpleado].tickets += 1
      totalGeneral += monto
      totalTickets += 1
      if (ticket.tipo_pago === "E") {
        recaudacionPorEmpleado[nombreEmpleado].efectivo += monto
        totalEfectivo += monto
      } else if (ticket.tipo_pago === "T") {
        recaudacionPorEmpleado[nombreEmpleado].transferencia += monto
        totalTransferencia += monto
      } else if (ticket.tipo_pago === "TC") {
        recaudacionPorEmpleado[nombreEmpleado].tarjeta += monto
        totalTarjeta += monto
      }
    })

    const resultado = Object.values(recaudacionPorEmpleado).sort((a, b) => b.total - a.total)
    setEmployeeRecaudacion(resultado)
    setTotalesGenerales({ totalGeneral, totalEfectivo, totalTransferencia, totalTarjeta, totalTickets })
    setShowEmployeeModal(true)
  }

  return (
    <div className="recaudaciones-container">
      <div className="header-container">
        <div>
          <h1 className="dashboard-title">Recaudaciones</h1>
          <p className="dashboard-subtitle">Monitoreo de ingresos del parking</p>
        </div>
        <button className="icon-button refresh-button" onClick={handleRefresh} disabled={refreshing} title="Actualizar">
          <RefreshCw className={`${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="filters-container">
        <div className="filters-wrapper">
          <div className="tabs-container">
            <button
              className={`tab-button ${timeRange === "hoy" ? "active" : ""}`}
              onClick={() => handleTimeRangeChange("hoy")}
            >
              Hoy
            </button>
            <button
              className={`tab-button ${timeRange === "ayer" ? "active" : ""}`}
              onClick={() => handleTimeRangeChange("ayer")}
            >
              Ayer
            </button>
            <button
              className={`tab-button ${timeRange === "semana" ? "active" : ""}`}
              onClick={() => handleTimeRangeChange("semana")}
            >
              Esta semana
            </button>
            <button
              className={`tab-button ${timeRange === "mes" ? "active" : ""}`}
              onClick={() => handleTimeRangeChange("mes")}
            >
              Este mes
            </button>
            <button
              className={`tab-button ${timeRange === "personalizado" ? "active" : ""}`}
              onClick={() => handleTimeRangeChange("personalizado")}
            >
              Personalizado
            </button>
          </div>

          {(timeRange === "personalizado" || showCalendar) && (
            <div className="date-range-container" ref={calendarRef}>
              <button
                className="custom-button outline-button date-range-button"
                onClick={() => setShowCalendar(!showCalendar)}
              >
                <CalendarIcon className="icon-small" />
                {dateRange.from ? (
                  dateRange.to ? (
                    <>
                      {format(dateRange.from, "dd/MM/yyyy")} - {format(dateRange.to, "dd/MM/yyyy")}
                    </>
                  ) : (
                    format(dateRange.from, "dd/MM/yyyy")
                  )
                ) : (
                  "Seleccionar fechas"
                )}
              </button>

              {showCalendar && (
                <div className="calendar-popover">
                  <SimpleCalendar
                    selectedRange={dateRange}
                    onSelectRange={setDateRange}
                    onClose={() => setShowCalendar(false)}
                  />
                </div>
              )}
            </div>
          )}

          <div className="filter-container">
            <select
              className="search-input"
              style={{ width: "200px", paddingLeft: "15px", marginLeft: "10px" }}
              value={selectedTarifa}
              onChange={(e) => setSelectedTarifa(e.target.value)}
            >
              <option value="Todas">Todas las tarifas</option>
              {tiposTarifa.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="search-container">
            <div className="search-input-wrapper">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Buscar por placa o empleado..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="export-buttons-container">
            <button
              className="custom-button outline-button"
              onClick={exportToExcel}
              disabled={filteredTickets.length === 0}
              title="Exportar a Excel"
            >
              <Download className="icon-small" />
              Excel
            </button>
            <button
              className="custom-button outline-button"
              onClick={exportToPDF}
              disabled={filteredTickets.length === 0}
              title="Exportar a PDF"
            >
              <FileText className="icon-small" />
              PDF
            </button>
          </div>
        </div>
      </div>

      <div className="stats-container">
        {/* ===== MODIFICACIÓN CLAVE AQUÍ ===== */}
        <div className="stat-card clickable-card" onClick={calcularRecaudacionPorEmpleado}>
          <div className="stat-card-header">
            <h3 className="stat-card-title">Total Recaudado</h3>
          </div>
          <div className="stat-card-content">
            <div className="stat-value">${stats.totalRecaudado.toFixed(2)}</div>
            <p className="stat-description">{stats.totalTickets} tickets procesados</p>
          </div>
          <button
            className="eye-button"
            onClick={(e) => {
              e.stopPropagation(); // Previene que el click en el botón también active el click de la tarjeta
              calcularRecaudacionPorEmpleado();
            }}
            aria-label="Ver detalle por empleado"
          >
            <Eye size={18} />
          </button>
        </div>
        {/* ==================================== */}

        <div className="stat-card">
          <div className="stat-card-header">
            <h3 className="stat-card-title">Efectivo</h3>
          </div>
          <div className="stat-card-content">
            <div className="stat-value efectivo-value">${stats.efectivo.toFixed(2)}</div>
            <p className="stat-description">
              {stats.ticketsEfectivo} tickets (
              {stats.totalTickets > 0 ? ((stats.ticketsEfectivo / stats.totalTickets) * 100).toFixed(0) : 0}%)
            </p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <h3 className="stat-card-title">Transferencia</h3>
          </div>
          <div className="stat-card-content">
            <div className="stat-value transferencia-value">${stats.transferencia.toFixed(2)}</div>
            <p className="stat-description">
              {stats.ticketsTransferencia} tickets (
              {stats.totalTickets > 0 ? ((stats.ticketsTransferencia / stats.totalTickets) * 100).toFixed(0) : 0}%)
            </p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <h3 className="stat-card-title">Tarjeta</h3>
          </div>
          <div className="stat-card-content">
            <div className="stat-value tarjeta-value">${stats.tarjetaCredito.toFixed(2)}</div>
            <p className="stat-description">
              {stats.ticketsTC} tickets (
              {stats.totalTickets > 0 ? ((stats.ticketsTC / stats.totalTickets) * 100).toFixed(0) : 0}%)
            </p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <h3 className="stat-card-title">Tiempo Promedio</h3>
          </div>
          <div className="stat-card-content">
            <div className="stat-value">
              {Math.floor(stats.promedioEstadia / 60)}h {Math.round(stats.promedioEstadia % 60)}m
            </div>
            <p className="stat-description">Estadía promedio por vehículo</p>
          </div>
        </div>
      </div>

      <div className="charts-container">
        <div className="chart-card">
          <div className="chart-card-header">
            <h3 className="chart-card-title">Recaudación Diaria</h3>
            <p className="chart-card-description">Desglose de ingresos por día</p>
          </div>
          <div className="chart-card-content">
            {loading ? (
              <div className="skeleton-chart"></div>
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fecha" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`$${value.toFixed(2)}`, ""]} />
                  <Legend />
                  <Bar dataKey="efectivo" name="Efectivo" stackId="a" fill="#1775ad" />
                  <Bar dataKey="transferencia" name="Transferencia" stackId="a" fill="#933155" />
                  <Bar dataKey="tarjetaCredito" name="Tarjeta" stackId="a" fill="#e17b1d" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data-message">
                <p>No hay datos disponibles para el período seleccionado</p>
              </div>
            )}
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-card-header">
            <h3 className="chart-card-title">Distribución de Pagos</h3>
            <p className="chart-card-description">Porcentaje por tipo de pago</p>
          </div>
          <div className="chart-card-content">
            {loading ? (
              <div className="skeleton-chart"></div>
            ) : stats.totalRecaudado > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={paymentChartData} cx="50%" cy="50%" labelLine={false} outerRadius={100} fill="#8884d8" dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {paymentChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`$${value.toFixed(2)}`, ""]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data-message">
                <p>No hay datos para mostrar</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="transactions-card">
        <div className="transactions-card-header">
          <h3 className="transactions-card-title">Transacciones</h3>
          <p className="transactions-card-description">{filteredTickets.length} registros encontrados</p>
        </div>
        <div className="transactions-card-content">
          {loading ? (
            <div className="skeleton-table">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="skeleton-row"></div>
              ))}
            </div>
          ) : filteredTickets.length > 0 ? (
            <div className="table-responsive">
              <table className="custom-table">
                <thead>
                  <tr>
                    <th>N°</th>
                    <th>ID</th>
                    <th>Placa</th>
                    <th>Entrada</th>
                    <th>Salida</th>
                    <th>Duración</th>
                    <th>Monto</th>
                    <th>Tipo Pago</th>
                    <th>Registró Entrada</th>
                    <th>Registró Salida</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.map((ticket, index) => {
                    const entrada = ticket.hentrada ? parseISO(ticket.hentrada) : null
                    const salida = ticket.hsalida ? parseISO(ticket.hsalida) : null
                    const duracion = entrada && salida ? differenceInMinutes(salida, entrada) : null
                    return (
                      <tr key={ticket.id}>
                        <td>{index + 1}</td>
                        <td>{ticket.id}</td>
                        <td className="font-medium">{ticket.placa}</td>
                        <td>{entrada ? format(entrada, "dd/MM/yyyy HH:mm") : "-"}</td>
                        <td>{salida ? format(salida, "dd/MM/yyyy HH:mm") : "-"}</td>
                        <td>{duracion !== null ? `${Math.floor(duracion / 60)}h ${duracion % 60}m` : "-"}</td>
                        <td className="font-medium">${ticket.monto?.toFixed(2) || "0.00"}</td>
                        <td>
                          <span
                            className={`badge ${
                              ticket.tipo_pago === "E"
                                ? "badge-success"
                                : ticket.tipo_pago === "T"
                                ? "badge-info"
                                : "badge-tarjeta"
                            }`}
                          >
                            {ticket.tipo_pago === "E"
                              ? "Efectivo"
                              : ticket.tipo_pago === "T"
                              ? "Transferencia"
                              : "Tarjeta"}
                          </span>
                        </td>
                        <td>{ticket.registro_entrada || "-"}</td>
                        <td>{ticket.registro_salida || "-"}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data-container">
              <p className="no-data-message">No se encontraron registros para el período y búsqueda seleccionados</p>
              <button className="custom-button outline-button" onClick={() => setSearchTerm("")}>
                <Filter className="icon-small" />
                Limpiar búsqueda
              </button>
            </div>
          )}
        </div>
      </div>

      <RecaudacionModal
        isOpen={showEmployeeModal}
        onClose={() => setShowEmployeeModal(false)}
        data={employeeRecaudacion}
        totales={totalesGenerales}
      />
    </div>
  )
}

const RecaudacionModal = ({ isOpen, onClose, data, totales }) => {
  if (!isOpen) return null

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-container-large">
        <div className="modal-header">
          <h3 className="modal-title">Recaudación por Empleado de Cierre</h3>
          <button className="modal-close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="modal-summary">
          <div className="summary-card">
            <h4>Total Recaudado</h4>
            <div className="summary-value">${totales.totalGeneral.toFixed(2)}</div>
          </div>
          <div className="summary-card">
            <h4>Total Efectivo</h4>
            <div className="summary-value efectivo-value">${totales.totalEfectivo.toFixed(2)}</div>
          </div>
          <div className="summary-card">
            <h4>Total Transferencia</h4>
            <div className="summary-value transferencia-value">${totales.totalTransferencia.toFixed(2)}</div>
          </div>
          <div className="summary-card">
            <h4>Total Tarjeta</h4>
            <div className="summary-value tarjeta-value">${totales.totalTarjeta.toFixed(2)}</div>
          </div>
        </div>

        <div className="modal-content">
          {data.length > 0 ? (
            <table className="custom-table">
              <thead>
                <tr>
                  <th>Empleado</th>
                  <th>Total Recaudado</th>
                  <th>Efectivo</th>
                  <th>Transferencia</th>
                  <th>Tarjeta</th>
                  <th>N° Tickets</th>
                </tr>
              </thead>
              <tbody>
                {data.map((empleado, index) => (
                  <tr key={index}>
                    <td>{empleado.nombre}</td>
                    <td className="font-medium">${empleado.total.toFixed(2)}</td>
                    <td className="efectivo-value">${empleado.efectivo.toFixed(2)}</td>
                    <td className="transferencia-value">${empleado.transferencia.toFixed(2)}</td>
                    <td className="tarjeta-value">${empleado.tarjeta.toFixed(2)}</td>
                    <td>{empleado.tickets}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="no-data-message">No hay datos de recaudación disponibles.</p>
          )}
        </div>

        <div className="modal-footer">
          <button className="custom-button outline-button" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

export default Recaudaciones